import { QueryKeys } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IProfileAddressModel, IProfileModel, IRewardPoint, IRewardPointTransaction, RewardBanner } from '@/interfaces/profile';
import {
  deleteProfileAddress,
  getProfile,
  getProfileAddressList,
  getRewardPageBanner,
  getRewardPoint,
  getRewardPointsTransaction,
  postFeedback,
  updateProfile,
  updateProfileAddress,
} from '@/services/profile.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      openNotification('success', 'Profile', 'Profile Update Successfully');
      queryClient.invalidateQueries({ queryKey: [QueryKeys.profileData] });
    },
    onError: (data: any) => {
      // openNotification('danger', 'Profile', data.error);
    },
  });
};

export const useGetProfileAddressList = (session?: INextAuthSessionModel | null) => {
  return useQuery<IProfileAddressModel[]>({
    queryKey: [QueryKeys.addressLists],
    queryFn: async () => {
      const addressList = await getProfileAddressList(session);
      return addressList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!session,
  });
};
export const useGetProfile = (session?: INextAuthSessionModel | null, shouldCallProfile: boolean = true) => {
  return useQuery<IProfileModel>({
    queryKey: [QueryKeys.profileData],
    queryFn: async () => {
      const profile = await getProfile(session);
      return profile;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!session && shouldCallProfile,
  });
};

export const useGetRewardPageBanner = () => {
  return useQuery<RewardBanner>({
    queryKey: [QueryKeys.reward],
    queryFn: async () => {
      const banner = await getRewardPageBanner();
      return banner;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useUpdateProfileAddress = (
  setShowAddressForm: Function,
  setSelectedShippingAddress?: Function,
  homeDeliveryPageShippingAddress?: boolean,
  homeDeliveryPageBillingAddress?: boolean,
  setSelectedBillingAddress?: Function,
  onSuccessCallback?: (data: any) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfileAddress,
    onSuccess: (data) => {
      if (homeDeliveryPageShippingAddress) {
        setSelectedShippingAddress?.(data);
      }
      if (homeDeliveryPageBillingAddress) setSelectedBillingAddress?.(data);

      queryClient.invalidateQueries({ queryKey: [QueryKeys.addressLists] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.applicableObjectiveBasedCampaigns],
      });
      setShowAddressForm(false);
      onSuccessCallback?.(data);
      openNotification('success', 'Address', 'Profile Address Updated Successfully');
    },
    onError: (data: any) => {
      openNotification('danger', 'Update Error', data.data.Messages[0]);
    },
  });
};
export const useDeleteAddress = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => {
      return deleteProfileAddress(addressId, customerId);
    },
    onSuccess: (data) => {
      openNotification('success', 'Address', 'Address Deleted Successfully');
      queryClient.invalidateQueries({ queryKey: [QueryKeys.addressLists] });
    },
    onError: (data: any) => {
      openNotification('danger', 'Delete Error', data.data.Messages[0]);
    },
  });
};
export const useGetRewardPoint = (session?: INextAuthSessionModel | null) => {
  return useQuery<IRewardPoint>({
    queryKey: [QueryKeys.rewardPoints],
    queryFn: async () => {
      const rewardPoint = await getRewardPoint(session);
      return rewardPoint;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!session,
  });
};
export const useGetRewardPointTransaction = (
  pageSize: number,
  pageNumber: number,
  session?: INextAuthSessionModel | null,
  showPurchaseOnly?: boolean,
) => {
  return useQuery<IRewardPointTransaction>({
    queryKey: [QueryKeys.rewardPointsTransaction + pageNumber],
    queryFn: async () => {
      const rewardPointsTransaction = await getRewardPointsTransaction(pageSize, pageNumber, session, showPurchaseOnly);
      return rewardPointsTransaction;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!session,
    placeholderData: (previousData) => previousData,
  });
};

export const usePostFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postFeedback,
  });
};
